import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import i18n from '../../../i18n';
import { emailCopySx, emailRowSx, emailValueSx } from './headerTriggers.styles';

// How long the tick stays after a copy.
const COPIED_MS = 1500;

/**
 * The signed-in user's email in the account menu: shown in full on hover and
 * copied with one click. It is changed through the profile, not here.
 */
export default function AccountEmailRow({ email }) {
    const [state, setState] = useState(null); // null | 'copied' | 'failed'
    const timer = useRef(null);

    useEffect(() => () => clearTimeout(timer.current), []);

    if (!email) return null;

    const copy = async () => {
        clearTimeout(timer.current);
        try {
            await navigator.clipboard.writeText(email);
            setState('copied');
        } catch {
            setState('failed');
        }
        timer.current = setTimeout(() => setState(null), COPIED_MS);
    };

    let tooltip = i18n.t('common.copy');
    if (state === 'copied') tooltip = i18n.t('common.copied');
    else if (state === 'failed') tooltip = i18n.t('text.copyFailed');

    return (
        <Box sx={emailRowSx}>
            {/* The row truncates rather than widening the panel; `title`
                carries the full address. */}
            <Box component="span" sx={emailValueSx} title={email}>
                {email}
            </Box>
            <Tooltip title={tooltip}>
                <IconButton
                    onClick={copy}
                    aria-label={tooltip}
                    sx={emailCopySx(state === 'copied')}
                >
                    {state === 'copied' ? (
                        <CheckRoundedIcon fontSize="inherit" />
                    ) : (
                        <ContentCopyRoundedIcon fontSize="inherit" />
                    )}
                </IconButton>
            </Tooltip>
        </Box>
    );
}

AccountEmailRow.propTypes = {
    email: PropTypes.string
};
